/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useRef, useState } from 'react';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Marker, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { EIconTypes } from 'constants/Icons';
import { EButtonVariants } from 'constants/Buttons';
import 'leaflet-fullscreen/dist/Leaflet.fullscreen.js';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import SearchFilterStore from 'state/SearchFilterStore';
import { IAccessory, IAmmunition, IAncillary, IComponent } from 'models/ancillaries';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import MapLegalityDropdown from 'pages/Firearms/RegisteredFirearmsMapPage/MapLegalityDropdown';
import { LEGAL_ID } from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmsLocationMap';
import AmmunitionDataSidebar from 'pages/AncillariesPage/AncillariesMap/AncillariesLocationMap/AmmunitionDataSidebar';
import ComponentDataSidebar, {
  ELegalityFilter,
} from 'pages/AncillariesPage/AncillariesMap/AncillariesLocationMap/ComponentDataSidebar';
import { EArtAncillaryTypeName } from 'constants/ArtAccessoryType';
import AccessoryDataSidebar from 'pages/AncillariesPage/AncillariesMap/AncillariesLocationMap/AncillaryDataSidebar';
import AncillariesDataSidebar from 'pages/AncillariesPage/AncillariesMap/AncillariesLocationMap/AllAncillariesDataSidebar';
import {
  ButtonFullScreen,
  ButtonFullScreenExit,
  ButtonLocateScreen,
  ButtonZoomIn,
  ButtonZoomOut,
  StyledLocationMapWrapper,
  StyledMapContainer,
} from './style';
import { EMarkerType, MarkerIcons } from './helper';
import { IMapBounds } from 'models/map';
import { MAP_MAXIMUM_CLUSTER_SIZE_FOR_SPIDERLY } from 'constants/Map';
import { LegalityTypes } from 'constants/LegalityTypes';
import { addLayersChunked } from 'utils/mapLayers';
import { EAssetType } from 'models/assetType';

const AncillaryIcon = (isLegal: boolean, type: EMarkerType) =>
  L.icon({
    iconUrl: isLegal ? MarkerIcons(type).Legal : MarkerIcons(type).Illegal,
    iconSize: [32, 39],
    iconAnchor: [16, 39],
    popupAnchor: [0, 0],
  });

interface IAncillariesLocationMap {
  accessories: IAccessory[];
  ammunition: IAmmunition[];
  components: IComponent[];
  ancillaries: IAncillary[];
  showLegalityBar: boolean;
  defaultLegality: string | undefined;
  isOnline: boolean;
  latitude?: number;
  longitude?: number;
  legalityUniqueId?: string;
  type?: string;

  updateLegality(id: string): void;
}

const AncillariesLocationMap = ({
  accessories,
  ammunition,
  components,
  ancillaries,
  updateLegality,
  defaultLegality = '',
  showLegalityBar = true,
  latitude,
  longitude,
  type,
  legalityUniqueId,
  isOnline,
}: IAncillariesLocationMap) => {
  (window as any).xAccessories = accessories; // fix for leaflet event function/no access to props
  (window as any).xAmmunition = ammunition; // fix for leaflet event function/no access to props
  (window as any).xComponents = components; // fix for leaflet event function/no access to props
  (window as any).xAncillaries = ancillaries; // fix for leaflet event function/no access to props
  const mapRef = useRef<L.Map | null>(null);
  const [clusterLayer, setClusterLayer] = useState<any>(null);
  const { legality, getLegality } = useDictionary();
  const [fullScreenState, setFullScreenState] = useState<boolean>(false);
  const legalityRef = useRef('');
  const addingGenerationRef = useRef(0);
  const { selectedLanguage } = useLang();

  const initialViewRef = useRef<{
    center: L.LatLng;
    zoom: number;
  } | null>(null);

  const url = isOnline
    ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    : `${process.env.REACT_APP_MAP_TILE_URL}tile/{z}/{x}/{y}.png`;

  useEffect(() => {
    (async function init() {
      legalityRef.current = defaultLegality;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async function initDictionaries() {
      await getLegality();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (!clusterLayer || latitude) return;

    const markersToAdd: L.Marker[] = [];

    ancillaries.forEach((ancillary) =>
      addMarker(
        ancillary,
        showAncillariesDataSidebarByMarkers,
        EMarkerType.default,
        markersToAdd,
      ),
    );
    accessories.forEach((accessory) =>
      addMarker(accessory, showAccessoriesDataSidebar, EMarkerType.accessories, markersToAdd),
    );
    ammunition.forEach((ammo) =>
      addMarker(ammo, showAmmunitionDataSidebar, EMarkerType.ammunition, markersToAdd),
    );
    components.forEach((component) =>
      addMarker(component, showComponentsDataSidebar, EMarkerType.components, markersToAdd),
    );

    return addLayersChunked(clusterLayer, markersToAdd, addingGenerationRef);
  }, [ancillaries, accessories, components, ammunition, latitude, clusterLayer]);

  const addMarker = (
    item: IAncillary | IComponent | IAmmunition | IAccessory,
    showItemSidebar: (markers: any[]) => void,
    markerType: EMarkerType,
    markersToAdd: any[],
  ) => {
    if (item.latitude && item.longitude) {
      const isLegal = item.legalityUniqueId === LegalityTypes.LEGAL;
      const marker = L.marker([item.latitude as number, item.longitude as number], {
        icon: AncillaryIcon(isLegal, markerType),
        alt: `${item.uniqueId},${item.legalityUniqueId}`,
      });

      marker.on('click', (e) => {
        showItemSidebar([e.target]);
        SearchFilterStore.setMapBounds(undefined);
      });

      marker.on('mouseover', (e) => {
        const isMarkerLegal = e.target.options.alt.split(',')[1] === LegalityTypes.LEGAL;
        e.target.setIcon(
          L.icon({
            iconUrl: isMarkerLegal
              ? MarkerIcons(getIconTypeByType(type)).LegalHover
              : MarkerIcons(getIconTypeByType(type)).IllegalHover,
            iconSize: [32, 39],
            iconAnchor: [16, 39],
            popupAnchor: [0, 0],
          }),
        );
      });

      marker.on('mouseout', (e) => {
        const isMarkerLegal = e.target.options.alt.split(',')[1] === LegalityTypes.LEGAL;
        e.target.setIcon(
          L.icon({
            iconUrl: isMarkerLegal
              ? MarkerIcons(getIconTypeByType(type)).Legal
              : MarkerIcons(getIconTypeByType(type)).Illegal,
            iconSize: [32, 39],
            iconAnchor: [16, 39],
            popupAnchor: [0, 0],
          }),
        );
      });

      markersToAdd.push(marker);
    }
  };

  const ChangeView = () => {
    const map = useMap();
    if (map) {
      mapRef.current = map;
    }
    const bounds: [number, number][] = [];
    if (!latitude && !longitude) {
      if (type === EArtAncillaryTypeName.ACCESSORY.toLowerCase()) {
        accessories.map((accessory) => {
          if (accessory.latitude && accessory.longitude) {
            bounds.push([accessory.latitude, accessory.longitude]);
          }
          return false;
        });
      } else if (type === EArtAncillaryTypeName.AMMUNITION.toLowerCase()) {
        ammunition.map((singleAmmunition) => {
          if (singleAmmunition.latitude && singleAmmunition.longitude) {
            bounds.push([singleAmmunition.latitude, singleAmmunition.longitude]);
          }
          return false;
        });
      } else if (type === EArtAncillaryTypeName.COMPONENTS.toLowerCase()) {
        components.map((component) => {
          if (component.latitude && component.longitude) {
            bounds.push([component.latitude, component.longitude]);
          }
          return false;
        });
      }
    }

    if (latitude && longitude) {
      bounds.push([latitude, longitude]);
    }
    if (bounds.length) {
      map.fitBounds(bounds);
    }
    if (map.getZoom() > 10) {
      map.setZoom(10);
    }
    if (!initialViewRef.current && map.getBounds().isValid() && map.getCenter()?.lat) {
      initialViewRef.current = {
        center: map.getCenter(),
        zoom: map.getZoom(),
      };
    }

    return null;
  };

  const zoomMap = (isZoomIn: boolean) => {
    if (mapRef.current) {
      mapRef.current.setZoom(
        isZoomIn ? mapRef.current.getZoom() + 1 : mapRef.current.getZoom() - 1,
      );
    }
  };

  const toggleFullScreen = () => {
    if (mapRef.current) {
      (mapRef as any).current.toggleFullscreen();
      setFullScreenState((prevState) => !prevState);
    }
  };

  const defaultZoom = () => {
    if (mapRef.current && initialViewRef.current) {
      mapRef.current.setView(initialViewRef.current.center, initialViewRef.current.zoom, {
        animate: true,
      });
      SearchFilterStore.setFirearms([]);
    }
  };

  const updateCurrentLegality = (value: string) => {
    legalityRef.current = value;
    updateLegality(value);
  };

  const getClusterClassName = () => {
    if (!legalityRef.current) {
      return '';
    }
    return legalityRef.current === LEGAL_ID
      ? 'custom-marker-cluster-legal'
      : 'custom-marker-cluster-illegal';
  };

  const createClusterCustomIcon = (cluster: any) =>
    L.divIcon({
      html: `<span>${cluster.getChildCount()}</span>`,
      className: `custom-marker-cluster ${getClusterClassName()}`,
      iconSize: L.point(56, 56, true),
    });

  const showAccessoriesDataSidebar = (markers: any[]) => {
    const accessoriesIds = new Set(markers.map((marker) => marker.options.alt.split(',')[0]));
    const accessoriesList = (window as any).xAccessories;
    SearchFilterStore.setAccessories(
      accessoriesList.filter((accessory: IAccessory) =>
        accessoriesIds.has(accessory.uniqueId),
      ),
    );
  };

  const showAmmunitionDataSidebar = (markers: any[]) => {
    const ammunitionIds = new Set(markers.map((marker) => marker.options.alt.split(',')[0]));
    const ammunitionList = (window as any).xAmmunition;
    SearchFilterStore.setAmmunition(
      ammunitionList.filter((singleAmmunition: IAmmunition) =>
        ammunitionIds.has(singleAmmunition.uniqueId),
      ),
    );
  };

  const showComponentsDataSidebar = (markers: any[]) => {
    const componentIds = new Set(markers.map((marker) => marker.options.alt.split(',')[0]));
    const componentsList = (window as any).xComponents;
    SearchFilterStore.setComponents(
      componentsList.filter((component: IComponent) => componentIds.has(component.uniqueId)),
    );
  };

  const showAncillariesDataSidebarByMarkers = (markers: any) => {
    const ancillariesIds = markers.length
      ? markers.map((marker: any) => marker.options.alt.split(',')[0])
      : markers.options.alt;
    const ancillariesList = (window as any).xAncillaries;
    SearchFilterStore.setAncillaries(
      ancillariesList.filter((ancillary: IAncillary) =>
        ancillariesIds.has(ancillary.uniqueId),
      ),
    );
  };

  const showAncillariesDataSidebar = () => {
    const ancillariesList = (window as any).xAncillaries;
    SearchFilterStore.setAncillaries(ancillariesList);
  };

  const showSingleAccessoryDataSidebar = () => {
    const accessoriesList = (window as any).xAccessories;
    SearchFilterStore.setAccessories(accessoriesList);
  };

  const showSingleAmmunitionDataSidebar = () => {
    const ammunitionList = (window as any).xAmmunition;
    SearchFilterStore.setAmmunition(ammunitionList);
  };

  const showSingleComponentDataSidebar = () => {
    const componentsList = (window as any).xComponents;
    SearchFilterStore.setComponents(componentsList);
  };

  /* eslint no-else-return: "error" */
  const getIconTypeByType = (ancillaryType: string | undefined) => {
    if (ancillaryType === EArtAncillaryTypeName.ACCESSORY.toLowerCase()) {
      return EMarkerType.accessories;
    } else if (ancillaryType === EArtAncillaryTypeName.AMMUNITION.toLowerCase()) {
      return EMarkerType.ammunition;
    } else if (ancillaryType === EArtAncillaryTypeName.COMPONENTS.toLowerCase()) {
      return EMarkerType.components;
    }
    return EMarkerType.default;
  };

  return (
    <StyledLocationMapWrapper>
      {accessories && <AccessoryDataSidebar />}
      {ammunition && <AmmunitionDataSidebar />}
      {components && <ComponentDataSidebar />}
      {ancillaries && <AncillariesDataSidebar />}
      <StyledMapContainer center={[0, 0]} zoom={2} scrollWheelZoom zoomControl={false}>
        {showLegalityBar && (
          <MapLegalityDropdown
            updateLegality={updateCurrentLegality}
            legality={legality}
            label="ancillaries.ancillariesLocation.legality"
          />
        )}
        <ChangeView />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={url}
        />
        <MarkerClusterGroup
          ref={setClusterLayer}
          showCoverageOnHover={false}
          spiderfyOnMaxZoom={false}
          zoomToBoundsOnClick={false}
          iconCreateFunction={createClusterCustomIcon}
          onClick={(e: any) => {
            const cluster = e.layer;

            const bounds = cluster.getBounds();

            const mapBounds: IMapBounds = {
              minLatitude: bounds.getSouth(),
              maxLatitude: bounds.getNorth(),
              minLongitude: bounds.getWest(),
              maxLongitude: bounds.getEast(),
            };

            if (type === EArtAncillaryTypeName.ACCESSORY.toLowerCase()) {
              mapBounds.assetType = EAssetType.ACCESSORY;
              showAccessoriesDataSidebar(cluster.getAllChildMarkers());
            } else if (type === EArtAncillaryTypeName.AMMUNITION.toLowerCase()) {
              mapBounds.assetType = EAssetType.AMMUNITION;
              showAmmunitionDataSidebar(cluster.getAllChildMarkers());
            } else if (type === EArtAncillaryTypeName.COMPONENTS.toLowerCase()) {
              mapBounds.assetType = EAssetType.COMPONENT;
              showComponentsDataSidebar(cluster.getAllChildMarkers());
            } else {
              showAncillariesDataSidebarByMarkers(cluster.getAllChildMarkers());
            }

            SearchFilterStore.setMapBounds(mapBounds);

            const group = cluster._group;
            let bottom = cluster;

            while (bottom._childClusters.length === 1) {
              bottom = bottom._childClusters[0];
            }

            const isAtMaxZoomAndSameCount =
              bottom._zoom === group._maxZoom && bottom._childCount === cluster._childCount;

            if (isAtMaxZoomAndSameCount) {
              if (
                cluster.getAllChildMarkers().length <= MAP_MAXIMUM_CLUSTER_SIZE_FOR_SPIDERLY
              ) {
                cluster.spiderfy();
              }
            } else {
              cluster.zoomToBounds();
            }
          }}
        >
          {latitude && longitude && !ancillaries && (
            <Marker
              key={`${latitude}-${longitude}`}
              alt={`$${latitude}-${longitude}`}
              position={[latitude, longitude]}
              icon={AncillaryIcon(
                legalityUniqueId === ELegalityFilter.legal,
                getIconTypeByType(type),
              )}
              eventHandlers={{
                click: () => {
                  SearchFilterStore.setMapBounds(undefined);

                  if (type === EArtAncillaryTypeName.ACCESSORY.toLowerCase()) {
                    showSingleAccessoryDataSidebar();
                  } else if (type === EArtAncillaryTypeName.AMMUNITION.toLowerCase()) {
                    showSingleAmmunitionDataSidebar();
                  } else if (type === EArtAncillaryTypeName.COMPONENTS.toLowerCase()) {
                    showSingleComponentDataSidebar();
                  } else {
                    showAncillariesDataSidebar();
                  }
                },
                mouseover: (e) => {
                  e.target.setIcon(
                    L.icon({
                      iconUrl:
                        e.target.options.alt.split(',')?.[1] === LEGAL_ID
                          ? MarkerIcons(getIconTypeByType(type)).LegalHover
                          : MarkerIcons(getIconTypeByType(type)).IllegalHover,
                      iconSize: [32, 39],
                      iconAnchor: [16, 39],
                      popupAnchor: [0, 0],
                    }),
                  );
                },
                mouseout: (e) => {
                  e.target.setIcon(
                    L.icon({
                      iconUrl:
                        e.target.options.alt.split(',')?.[1] === LEGAL_ID
                          ? MarkerIcons(getIconTypeByType(type)).Legal
                          : MarkerIcons(getIconTypeByType(type)).Illegal,
                      iconSize: [32, 39],
                      iconAnchor: [16, 39],
                      popupAnchor: [0, 0],
                    }),
                  );
                },
              }}
            />
          )}
        </MarkerClusterGroup>
        <ButtonZoomIn
          icon={EIconTypes.plus1}
          variant={EButtonVariants.outlined}
          onClick={() => zoomMap(true)}
        />
        <ButtonZoomOut
          icon={EIconTypes.minus}
          variant={EButtonVariants.outlined}
          onClick={() => zoomMap(false)}
        />
        <ButtonLocateScreen
          icon={EIconTypes.locate}
          variant={EButtonVariants.outlined}
          onClick={() => defaultZoom()}
        />
        {!fullScreenState && (
          <ButtonFullScreen
            icon={EIconTypes.fullScreen}
            variant={EButtonVariants.outlined}
            onClick={() => toggleFullScreen()}
          />
        )}
        {fullScreenState && (
          <ButtonFullScreenExit
            icon={EIconTypes.fullScreenExit}
            variant={EButtonVariants.outlined}
            onClick={() => toggleFullScreen()}
          />
        )}
      </StyledMapContainer>
    </StyledLocationMapWrapper>
  );
};

export { AncillariesLocationMap };
